import { observer } from "mobx-react";
import React from "react";
import { FilterKey } from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import {
    CallDirectionsFilter,
    CallDurationFilter,
    MessageCountFilter,
    ClassifiersFilter,
    ContactTypeFilter,
    DateRangeFilter,
    DateReferenceFilter,
    DefaultFilter,
    EddyEffectFilter,
    EvaluationTypesFilter,
    ExtendedMetadataInputFilter,
    FavoriteConversationsFilter,
    HierarchiesFilter,
    HipaaComplianceFilter,
    MediaTypesFilter,
    SafetyEventFilter,
    SentimentFilter,
    ThemesFilter,
    TopicsFilter,
    PrimaryTopicsFilter,
    PrimaryDriversFilter,
    OutlineFilter,
    WordsAndPhrasesFilter,
    SemanticSearchFilter,
    ExtendedMetadataGroupedFilter,
} from ".";
import { AgentsFilter } from "../Agent/AgentsFilter";
import { AgentManagersFilter } from "../AgentManager/AgentManagersFilter";
import { IApplicationFilterComponent } from "./IApplicationFilterComponent";
import { ConversationLabelingFilter } from "../ConversationLabeling/ConversationLabelingFilter";
import { ModelResultsFilter } from "../ModelResults/ModelResultsFilter";
import { PatientJourneyStageFilter } from "../PatientJourney/PatientJourneyStageFilter";
import { ClientCallIdFilter } from "../ClientCallId/ClientCallIdFilter";
import { LanguageFilter } from "../Language/LanguageFilter";
import { ModuleScoresFilter } from "../ModuleScores/ModuleScoresFilter";
import { QuestionResultFilter } from "../QuestionResult/QuestionResultFilter";

const filterKeyToComponent: Record<
    FilterKey,
    React.FunctionComponent<IApplicationFilterComponent> | undefined
> = {
    callDuration: CallDurationFilter,
    minCallDuration: undefined,
    maxCallDuration: undefined,
    messageCount: MessageCountFilter,
    minMessageCount: undefined,
    maxMessageCount: undefined,
    startDate: undefined,
    endDate: undefined,
    dateRange: DateRangeFilter,
    dateReferenceOption: DateReferenceFilter,
    dateRangeLabel: undefined,
    evaluationTypes: EvaluationTypesFilter,
    hierarchyIds: HierarchiesFilter,
    selectedCategories: undefined,
    selectedSubcategories: undefined,
    agentIds: AgentsFilter,
    agentManagerIds: AgentManagersFilter,
    mediaTypes: MediaTypesFilter,
    callDirections: CallDirectionsFilter,
    clientCallId: ClientCallIdFilter,
    languages: LanguageFilter,
    meta1: DefaultFilter,
    meta2: DefaultFilter,
    meta3: DefaultFilter,
    meta4: DefaultFilter,
    meta5: DefaultFilter,
    extendedMetadata: ExtendedMetadataInputFilter,
    extendedMetadataGrouped: ExtendedMetadataGroupedFilter,
    beginningSentiment: SentimentFilter,
    endingSentiment: SentimentFilter,
    eddyEffectStatus: EddyEffectFilter,
    safetyEvent: SafetyEventFilter,
    hipaaCompliance: HipaaComplianceFilter,
    topics: TopicsFilter,
    themes: ThemesFilter,
    primaryTopics: PrimaryTopicsFilter,
    drivers: PrimaryDriversFilter,
    containsTopics: undefined,
    containsThemes: undefined,
    containsPrimaryTopics: undefined,
    containsDrivers: undefined,
    wordsAndPhrasesSearchString: WordsAndPhrasesFilter,
    wordsAndPhrasesItems: undefined,
    wordsAndPhrasesIsInputStandard: undefined,
    rbcFilterItems: ClassifiersFilter,
    outlineSegmentFilterItems: OutlineFilter,
    contactTypes: ContactTypeFilter,
    patientJourneyStages: PatientJourneyStageFilter,
    favoriteConversations: FavoriteConversationsFilter,
    containsDataLabels: undefined,
    containsResearchLabels: undefined,
    dataLabels: ConversationLabelingFilter,
    modelResults: ModelResultsFilter,
    moduleScores: ModuleScoresFilter,
    questionResults: QuestionResultFilter,
    embeddedTranscriptionQuery: SemanticSearchFilter,
    embeddedTranscriptionQueryThreshold: undefined,
};

type FilterKeysWithIds = Extract<FilterKey, "primaryTopics" | "drivers">;

const filterKeyToComponentId: Record<FilterKeysWithIds, string> = {
    primaryTopics: "signals-filters-primary-topic",
    drivers: "signals-filters-primary-driver",
};

export const FilterComponent = observer(function (
    props: IApplicationFilterComponent,
) {
    let InnerFilterComponent = filterKeyToComponent[props.filterKey];

    const isExtendedMetadataItem =
        props.filterKey.startsWith("extendedMetadata");
    if (props.isGroupableExtendedMetadata) {
        InnerFilterComponent = filterKeyToComponent["extendedMetadataGrouped"];
    } else if (isExtendedMetadataItem)
        InnerFilterComponent = filterKeyToComponent["extendedMetadata"];

    if (!InnerFilterComponent) {
        console.error(
            `Invalid or unsupported filter key: ${props.filterKey}. Nothing rendered.`,
        );
        return null;
    }

    return (
        <InnerFilterComponent
            {...props}
            id={filterKeyToComponentId[props.filterKey] ?? props.id}
        />
    );
});
